<template>
  <div class="tw-overflow-y-auto tw-h-full tw-pl-[15px]">
    <div class="tw-flex tw-items-center tw-justify-between tw-mb-4">
      <div class="tw-text-2xl tw-font-medium">
        {{ copy.todosTitle }}
      </div>
      <div>
        <SfButtonDs
          variant="primary"
          size="sm"
          data-testid="add-todo-button"
          @click="openTodoModal"
        >
          +
          {{ copy.addTodoButton }}
        </SfButtonDs>
      </div>
    </div>
    <div
      v-if="postMeetingRemarks.length === 0"
      class="
        tw-text-center
        tw-text-sf-neutral-dark-2
        tw-text-sm
        tw-bg-sf-snow
        tw-px-4
        tw-py-4
        tw-rounded-lg
        tw-mb-4
      "
    >
      {{ copy.noMessages }}
    </div>
    <div v-else class="tw-flex tw-flex-col tw-gap-4 tw-mb-4">
      <RemarkCard
        v-for="item in postMeetingRemarks"
        :key="item.id"
        :remark="item"
        part-of="past_meetings"
        show-period
        hide-read-status
        show-tags
        disable-tag-links
        :exclude-tag-ids-in-card="talkingPointTagIds"
        :show-context="true"
        :show-context-btn="true"
      >
        <template #custom-actions>
          <SfButtonDs
            v-if="!disableInteraction"
            size="sm"
            variant="secondary"
            class="tw-hidden group-hover:tw-block"
            @click.stop="openMoveBackConfirmationModal(item)"
          >
            <i class="fa fa-arrow-left" />
            {{ copy.moveBackToOpen }}
          </SfButtonDs>
        </template>
      </RemarkCard>
    </div>

    <div class="tw-text-2xl tw-font-medium tw-mb-4">
      {{ copy.messagesTitle }}
    </div>
    <div
      v-if="discussedRemarks.length === 0"
      class="
        tw-text-center
        tw-text-sf-neutral-dark-2
        tw-text-sm
        tw-bg-sf-snow
        tw-px-4
        tw-py-4
        tw-rounded-lg
        tw-mb-4
      "
    >
      {{ copy.noDiscussedMessages }}
    </div>

    <div v-else class="tw-flex tw-flex-col tw-gap-4">
      <RemarkCard
        v-for="item in discussedRemarks"
        :key="item.id"
        :remark="item"
        part-of="past_meetings"
        show-period
        hide-read-status
        show-tags
        disable-tag-links
        :exclude-tag-ids-in-card="talkingPointTagIds"
        :show-context="true"
        :show-context-btn="true"
      >
        <template #custom-actions>
          <SfButtonDs
            v-if="!disableInteraction"
            size="sm"
            variant="secondary"
            class="tw-hidden group-hover:tw-block"
            @click.stop="openMoveBackConfirmationModal(item)"
          >
            <i class="fa fa-arrow-left" />
            {{ copy.moveBackToOpen }}
          </SfButtonDs>
        </template>
      </RemarkCard>
    </div>
  </div>
</template>
<script setup lang="ts">
import { onBeforeUnmount, onMounted, toRef, inject } from 'vue'

import { SfLog } from '@app/adapters/logger'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type {
  Meeting,
  PastPageData,
} from '@app/application/client_meetings/types'
import { useTalkingPointTag } from '@app/application/client_meetings/composables/use-talking-point-tag'

import type { Remark } from '@app/application/communication/types'
import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import * as PrepareApi from '@app/application/client_meetings/api/prepare'
import { usePastMeetingsStore } from '@app/application/client_meetings/screens/past/stores/past-meetings'
import { useMeetingRemarks } from '@app/application/client_meetings/screens/past/composables/use-meeting-remarks'

const pastMeetingsStore = usePastMeetingsStore()

interface Props {
  meeting: Meeting
}

const props = defineProps<Props>()

const I18n = useI18n()
const copy = {
  todosTitle: I18n.t('client_meetings.past.todos.title'),
  addTodoButton: I18n.t('client_meetings.past.todos.add_todo_button'),
  noMessages: I18n.t('client_meetings.past.todos.no_messages'),
  moveBackToOpen: I18n.t('client_meetings.past.remarks.move_back_to_open'),
  messagesTitle: I18n.t('client_meetings.past.messages_title'),
  noDiscussedMessages: I18n.t('client_meetings.past.no_discussed_messages'),
}

const { discussedRemarks, postMeetingRemarks } = useMeetingRemarks(
  toRef(props, 'meeting')
)

const pageData = inject('pageData') as PastPageData

const { talkingPointTagIds } = useTalkingPointTag()

const disableInteraction = isCurrentWorkflowLocked()

function openMoveBackConfirmationModal(remark: Remark) {
  window.modalCitizen.openModalPortal('move-back-to-open-confirmation-modal', {
    remark,
  })
}

function openTodoModal(): void {
  window.modalCitizen.openModal('new-remark', {
    type: 'todo',
    ownerType: 'ClientMeeting',
    ownerId: props.meeting.id,
    clientMeetingId: props.meeting.id,
    hideTypeSelector: true,
    allRemarkTags: window.allRemarkTags,
    showTags: true,
    ledgerLabel: pageData.ledgerLabel,
    isClientMeetingRemark: true,
    selectedTagIds: talkingPointTagIds.value,
    title: I18n.t('client_meetings.past.todos.add_todo_title'),
    saveButtonLabel: I18n.t(
      'client_meetings.past.todos.add_todo_save_button_label'
    ),
  })
}
async function refreshCommunicationData(data?: {
  remark: Remark
  action?: string
}): Promise<void> {
  const isCreateRemarkForCurrentMeeting =
    data?.action === 'createRemark' &&
    data?.remark.clientMeetingId === props.meeting.id

  if (!isCreateRemarkForCurrentMeeting) {
    return
  }

  const remarksOrder = [...props.meeting.order, `Remark|${data.remark.id}`]
  try {
    await PrepareApi.setOrder(remarksOrder, props.meeting.id)
  } catch (e) {
    SfLog.warn('Error while setting meeting remarks order', {
      extra: { error: e, meetingId: props.meeting.id },
    })
  }

  pastMeetingsStore.setMeetingOrder(props.meeting.id, remarksOrder)
}

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})
</script>
